import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function SettingsPage() {
  const [xeroStatus, setXeroStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Check Xero connection status on mount
  useEffect(() => {
    checkXeroStatus();
  }, []);

  const checkXeroStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/xero/status`);
      const data = await response.json();
      setXeroStatus({ ...data, loading: false });
    } catch (error) {
      console.error('Error checking Xero status:', error);
      setXeroStatus({ connected: false, loading: false });
    }
  };

  const handleConnectXero = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API_URL}/xero/connect`);
      const data = await response.json();
      
      if (data.url) {
        // Open Xero authorization in new window
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error connecting to Xero:', error);
      alert('Failed to connect to Xero. Please try again.');
      setConnecting(false);
    }
  };

  const handleDisconnectXero = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Xero?')) {
      return;
    }

    setDisconnecting(true);
    try {
      const response = await fetch(`${API_URL}/xero/disconnect`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setXeroStatus({ connected: false, loading: false });
        alert('Successfully disconnected from Xero');
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting from Xero:', error);
      alert('Failed to disconnect from Xero. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  // Check for connection success/error in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    if (params.get('xero_connected') === 'true') {
      alert('Successfully connected to Xero!');
      checkXeroStatus();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (params.get('xero_error')) {
      alert('Failed to connect to Xero. Please try again.');
      setConnecting(false);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '32px', fontWeight: '900', color: '#1976d2', marginBottom: '8px' }}>
        ⚙️ Settings
      </h1>
      <p style={{ color: '#666', marginBottom: '32px', fontSize: '16px' }}>
        Manage system integrations and configurations
      </p>

      {/* Xero Integration Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: '#13B5EA',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            💼
          </div>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', margin: 0 }}>
              Xero Integration
            </h2>
            <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
              Connect to Xero for automated invoice creation
            </p>
          </div>
        </div>

        {xeroStatus.loading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Loading connection status...
          </div>
        ) : (
          <>
            <div style={{
              padding: '16px',
              background: xeroStatus.connected ? '#e8f5e9' : '#fff3e0',
              borderRadius: '8px',
              marginBottom: '16px',
              border: `2px solid ${xeroStatus.connected ? '#4caf50' : '#ff9800'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '20px' }}>
                  {xeroStatus.connected ? '✅' : '⚠️'}
                </span>
                <strong style={{ fontSize: '16px', color: '#333' }}>
                  Status: {xeroStatus.connected ? 'Connected' : 'Not Connected'}
                </strong>
              </div>
              
              {xeroStatus.connected && xeroStatus.expiresAt && (
                <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 28px' }}>
                  Token expires: {new Date(xeroStatus.expiresAt).toLocaleString()}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              {!xeroStatus.connected ? (
                <button
                  onClick={handleConnectXero}
                  disabled={connecting}
                  style={{
                    padding: '12px 24px',
                    background: connecting ? '#ccc' : '#13B5EA',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: connecting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 8px rgba(19, 181, 234, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (!connecting) e.target.style.background = '#0FA3D1';
                  }}
                  onMouseOut={(e) => {
                    if (!connecting) e.target.style.background = '#13B5EA';
                  }}
                >
                  {connecting ? '🔄 Connecting...' : '🔗 Connect to Xero'}
                </button>
              ) : (
                <>
                  <button
                    onClick={checkXeroStatus}
                    style={{
                      padding: '12px 24px',
                      background: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#45a049'}
                    onMouseOut={(e) => e.target.style.background = '#4caf50'}
                  >
                    🔄 Refresh Status
                  </button>
                  <button
                    onClick={handleDisconnectXero}
                    disabled={disconnecting}
                    style={{
                      padding: '12px 24px',
                      background: disconnecting ? '#ccc' : '#d32f2f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: disconnecting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      if (!disconnecting) e.target.style.background = '#c62828';
                    }}
                    onMouseOut={(e) => {
                      if (!disconnecting) e.target.style.background = '#d32f2f';
                    }}
                  >
                    {disconnecting ? '🔄 Disconnecting...' : '🔌 Disconnect'}
                  </button>
                </>
              )}
            </div>

            {!xeroStatus.connected && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#666'
              }}>
                <strong>ℹ️ Note:</strong> You'll be redirected to Xero to authorize the connection. 
                After authorization, you'll be redirected back to this page.
              </div>
            )}
          </>
        )}
      </div>

      {/* Future settings sections can be added here */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        opacity: 0.6
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '8px' }}>
          📊 Additional Settings
        </h2>
        <p style={{ color: '#666', fontSize: '14px' }}>
          More configuration options coming soon...
        </p>
      </div>
    </div>
  );
}

export default SettingsPage;
